import { Editor } from '@tiptap/core';
import { TiptapEditorDirective } from 'ngx-tiptap';
import { FormsModule } from '@angular/forms';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { EditMessageService } from './editMessage/edit-message.service';
import { MessageContent } from '../../../../shared-api/messageContent';
import { FileNode } from './custom-nodes/custom-file-node/file.node';
import { EditFileNode } from './custom-nodes/edit-message.node';
import { ReplyService } from './replyMessage/reply-message.service';
import { ReplyNode } from './custom-nodes/reply.node';
import { MentionsService } from '../../../services/chat-message/mentions/mentions.service';
import { MentionRendererFactory } from './custom-nodes/custom-mention-node/custom-mention';
import { sanitizeMatrixHtml, withPrevious } from '../../../services/util';
import { SessionService } from '../../../services/session/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TypingService } from '../../../services/typing/typing.service';
import Image from '@tiptap/extension-image';
import { FileUploadComponent, PendingAttachment } from './file-upload/file-upload';
import { Component, DestroyRef, inject, Injector, input, OnDestroy, OnInit, output, viewChild } from '@angular/core';
import { MessageService } from '../../../services/message/message.service';
import { MediaService } from '../../../services/media/media.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TiptapEditorTestIds } from './tiptap-editor.test-ids';
import { RoomListAccessService } from '../../../services/room-list/room-list-access.service';
import { AccountService } from '../../../services/account/account.service';
import { CleanReplyPipe } from '../../../common/pipes/clean-reply.pipe';
import { RoomMembershipState } from '../../../../shared-api/roomMembershipState';
import { RoomMember as ModelRoomMember } from '../../../model/room-member';

@Component({
  selector: 'app-tiptap-editor',
  imports: [
    TiptapEditorDirective,
    FormsModule,
    NgClass,
    MatIcon,
    MatIconButton,
    MatTooltip,
    FileUploadComponent,
    AsyncPipe,
    MatProgressSpinner
  ],
  templateUrl: './tiptap-editor.component.html',
  styleUrl: './tiptap-editor.component.scss',
  standalone: true
})
/**
 * Diese Komponente stellt ein Eingabefeld für Chat-Nachrichten bereit, das auf dem tiptap-Editor basiert.
 * Es unterstützt Benutzererwähnungen und das Senden von Nachrichten an einen Matrix-Chatraum.
 *
 * Es wird folgende Anforderung in dieser Komponente erfüllt:
 * A_25437 - Erwähnungen
 * TI-M Clients MÜSSEN es ermöglichen, dass über das Eingabefeld andere Raumteilnehmer gemäß
 * [Client-Server API/#user-and-room-mentions] im jeweiligen Chatraum erwähnt werden können.
 * Dazu MUSS der TI-M Client eine entsprechende Nutzerliste anzeigen, sobald der Nutzer ein neues Wort mit "@" startet.
 */
export class TiptapEditor implements OnInit, OnDestroy {
  messageSent = output<void>();
  roomId = input.required<string>();
  roomIdWithPrev = withPrevious(this.roomId);
  value = ``;
  isEditing = false;
  originalMessageId: string | undefined;
  readonly fileUpload = viewChild(FileUploadComponent);
  isSending = false;
  isEncrypted = input<boolean>(true);
  protected readonly testIds = TiptapEditorTestIds;
  protected editMessageService = inject(EditMessageService);
  private readonly injector = inject(Injector);
  private readonly messageService: MessageService = inject(MessageService);
  private readonly replyService: ReplyService = inject(ReplyService);
  private readonly mentionsService: MentionsService = inject(MentionsService);
  private readonly typingService = inject(TypingService);
  private readonly rendererFactory: MentionRendererFactory = inject(MentionRendererFactory);
  private readonly roomListAccesService = inject(RoomListAccessService);
  private readonly accountService = inject(AccountService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly sessionService: SessionService = inject(SessionService);
  private readonly mediaService: MediaService = inject(MediaService);
  isUploading$ = this.mediaService.isUploadEnabled();
  private readonly roomContentsMap: Map<string, string> = new Map<string, string>();
  private pendingAttachments: PendingAttachment[] = [];
  private readonly CustomImage = Image.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        attachmentId: {
          default: null
        }
      };
    }
  });
  editor = new Editor({
    editorProps: {
      handleKeyDown: (view, event): boolean => {
        if (event.key === 'Enter' && event.ctrlKey) {
          const mentionPopup = document.querySelector('.mention-items, .mention-suggestion, [class*="mention"]');
          if (mentionPopup && mentionPopup.children.length > 0) {
            return false;
          }
          event.preventDefault();
          this.onSend();
          return true;
        }
        return false;
      },
      attributes: {
        class: 'tiptap-editor-content'
      }
    },
    onUpdate: ({ editor }): void => {
      void this.typingService.checkForTyping(editor.getText(), this.roomId());
    },
    extensions: [
      StarterKit,
      FileNode,
      this.CustomImage.configure({
        inline: false,
        allowBase64: true
      }),
      EditFileNode(this.injector),
      ReplyNode(this.injector),
      Placeholder.configure({
        placeholder: () =>
          this.isEncrypted() ? 'Verschlüsselte Nachricht eingeben' : 'Unverschlüsselte Nachricht eingeben'
      }),
      this.mentionsService.createMentionExtension(this.roomId, this.rendererFactory)
    ]
  });

  ngOnInit(): void {
    this.editMessageService.editRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => {
      this.isEditing = true;
      this.originalMessageId = message.eventId;
      this.editor.commands.setContent(sanitizeMatrixHtml(message.formattedBody));
      this.editor.commands.focus();
    });
    this.replyService.replyRequest$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => {
      const cleanPipe = new CleanReplyPipe();

      const attrs: any = {
        sender: message.sender,
        body: message.body,
        formattedBody: cleanPipe.transform(sanitizeMatrixHtml(message.formattedBody)),
        date: message.date,
        senderId: message.senderId,
        eventId: message.eventId,
        msgtype: message.content?.['msgtype'] || 'm.text'
      };

      if (message.content?.['msgtype']?.startsWith('m.file') || message.content?.['msgtype']?.startsWith('m.image')) {
        attrs.fileName = message.content['body'];
        attrs.fileSize = message.content['info']?.size;
        attrs.fileMimetype = message.content['info']?.mimetype;
        attrs.fileUrl = message.content['url'];
        attrs.fileIcon = 'file';
        attrs.msgtype = message.content['msgtype'];
      }

      this.editor.commands.insertContent({
        type: 'reply',
        attrs: attrs
      });
      this.editor.commands.focus();
    });

    this.sessionService
      .getSessionRoomId$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const { previous, current } = this.roomIdWithPrev();
        this.handleRoomChange(previous, current);
        if (this.isEditing) {
          this.cancelEdit();
        }
      });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  async onSend(): Promise<void> {
    if (this.isSending) return;

    if (this.isEditing && !this.originalMessageId) {
      throw new Error('No MessageID to reference to');
    }

    this.isSending = true;

    try {
      const attachments = this.syncAttachmentsWithEditor(this.pendingAttachments);

      const hasAttachments = attachments.length > 0;

      const initialTextBody = this.editor.getText().trim();
      const initialHtmlBody = this.editor.getHTML();

      const textBody = hasAttachments
        ? this.getTextWithoutAttachmentNames(initialTextBody, attachments)
        : initialTextBody;

      const htmlBody = hasAttachments ? this.getHtmlWithoutAttachmentNodes(initialHtmlBody) : initialHtmlBody;

      const hasContent = Boolean(textBody) || hasAttachments;

      if (!hasContent) return;

      if (this.isEditing) {
        await this.handleEdit(textBody, htmlBody);
        return;
      }

      await this.handleSend(textBody, htmlBody, attachments);
    } finally {
      this.isSending = false;
    }
    await this.typingService.cancelTypingNotification(this.roomId());
  }

  protected cancelEdit(): void {
    this.editor.commands.clearContent();
    this.fileUpload()?.reset();
    this.originalMessageId = undefined;
    this.isEditing = false;
  }

  protected onAttachmentSelected(attachment: PendingAttachment): void {
    this.pendingAttachments.push(attachment);
    if (attachment.kind === 'image') {
      this.editor
        .chain()
        .focus()
        .insertContent({
          type: 'image',
          attrs: {
            src: attachment.previewSrc,
            alt: attachment.name,
            title: attachment.name,
            attachmentId: attachment.id
          }
        })
        .run();

      return;
    }

    this.editor
      .chain()
      .focus()
      .insertContent({
        type: 'file',
        attrs: {
          attachmentId: attachment.id,
          name: attachment.name,
          mime: attachment.mime,
          size: attachment.size
        }
      })
      .run();
  }

  protected isRoomMember(): boolean {
    return this.getMyRoomMember()?.membershipState === RoomMembershipState.Join;
  }

  protected canWrite(): boolean {
    const user = this.getMyRoomMember();
    // Das Member-Modell kann nach createRoom/Raumwechsel kurz nachhängen. In diesem Fenster darf
    // canWrite() NICHT werfen — ein throw in der @if-Bedingung bricht die Change-Detection ab und
    // der Editor rendert gar nicht mehr. Stattdessen: noch keine Schreibrechte annehmen, der
    // nächste CD-Zyklus prüft erneut.
    if (!user) {
      return false;
    }
    return user.powerLevel >= 0;
  }

  /**
   * Eigenes Member-Modell des aktuellen Raums. Bewusst gemeinsame Quelle für isRoomMember() und
   * canWrite() (gleiche roomId, gleicher Store) — sonst entsteht ein Inkonsistenz-Fenster, in dem
   * Membership bereits Join meldet, das Member-Modell aber noch fehlt.
   */
  private getMyRoomMember(): ModelRoomMember | undefined {
    // getMyMxId() wirft, wenn (noch) keine userId existiert (Logout/Session-Teardown). Da dieser
    // Pfad in jeder Change-Detection über die @if-Bedingung des Editors läuft, hier abfangen —
    // ein throw in der @if-Bedingung würde die Change-Detection abbrechen und den View zerschießen.
    let userId: string;
    try {
      userId = this.accountService.getMyMxId();
    } catch {
      return undefined;
    }
    return this.roomListAccesService.getModelRoomMember(this.roomId(), userId);
  }

  private getHtmlWithoutAttachmentNodes(html: string): string {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    wrapper.querySelectorAll('.file-node').forEach(node => node.remove());
    wrapper.querySelectorAll('img').forEach(node => node.remove());

    return wrapper.innerHTML
      .replaceAll(/<p>\s*<\/p>/g, '')
      .replaceAll(/<p><br\s*\/?><\/p>/g, '')
      .trim();
  }

  private getTextWithoutAttachmentNames(text: string, attachments: PendingAttachment[]): string {
    let cleaned = text;

    for (const attachment of attachments) {
      cleaned = cleaned.replaceAll(attachment.name, '');
    }

    return cleaned.trim();
  }

  private async handleEdit(finalTextBody: string, finalHtmlBody: string): Promise<void> {
    const originalMessageId = this.originalMessageId;
    if (!originalMessageId) {
      throw new Error('No MessageID to reference to');
    }

    const content: MessageContent = {
      body: finalTextBody,
      msgtype: 'm.text',
      formatted_body: finalHtmlBody
    };

    try {
      await this.messageService.editMessage(this.roomId(), originalMessageId, content);
      this.resetEditorState();
      this.originalMessageId = undefined;
      this.isEditing = false;
    } catch (error) {
      console.error('Fehler beim Editieren der Nachricht:', error);
    }
  }

  private async handleSend(
    finalTextBody: string,
    finalHtmlBody: string,
    attachments: PendingAttachment[]
  ): Promise<void> {
    if (attachments.length === 0) {
      const replyNode = this.editor.getJSON()?.content?.find(node => node.type === 'reply');
      const replyEventId = replyNode?.attrs?.['eventId'];

      await this.sendTextMessage(finalTextBody, finalHtmlBody, replyEventId);

      this.resetEditorState();
      this.messageSent.emit();
      return;
    }

    try {
      await this.messageService.sendMessageWithAttachments(
        this.roomId(),
        attachments.map(attachment => attachment.file),
        finalTextBody,
        finalHtmlBody,
        this.getMentions()
      );

      this.resetEditorState();
      this.messageSent.emit();
    } catch (error) {
      console.error('Fehler beim Senden der Antwort:', error);
    }
  }

  /**
   * Versendet eine Textnachricht ohne Attachment. Reiner Text ohne Mentions/Reply läuft
   * über shared-api sendMessage (denselben Pfad testet der Testtreiber). Reply und
   * Mentions sind App-only-Erweiterungen (m.relates_to bzw. m.mentions sind im
   * Treibervertrag nicht darstellbar).
   */
  private async sendTextMessage(finalTextBody: string, finalHtmlBody: string, replyEventId?: string): Promise<void> {
    if (replyEventId) {
      try {
        await this.messageService.sendReply(this.roomId(), replyEventId, finalTextBody, finalHtmlBody);
      } catch (error) {
        console.error('Fehler beim Senden der Antwort:', error);
      }
      return;
    }

    const mentions = this.getMentions();
    try {
      if (mentions?.length) {
        await this.messageService.sendCustomMessage(this.roomId(), finalTextBody, finalHtmlBody, mentions);
      } else {
        const content: MessageContent = {
          msgtype: 'm.text',
          body: finalTextBody,
          ...(finalHtmlBody ? { format: 'org.matrix.custom.html', formatted_body: finalHtmlBody } : {})
        };
        await this.messageService.sendMessage(this.roomId(), content);
      }
    } catch (error) {
      console.error('Fehler beim Senden der Nachricht:', error);
    }
  }

  private getEditorContent(): string {
    return this.editor.getHTML();
  }

  private setEditorContent(content: string): void {
    if (this.editor) {
      this.editor.chain().setContent(sanitizeMatrixHtml(content)).focus().run();
    }
  }

  private saveRoomContent(roomId: string, content: string): void {
    this.roomContentsMap.set(roomId, content);
  }

  private getRoomContent(roomId: string): string | undefined {
    return this.roomContentsMap.get(roomId);
  }

  private handleRoomChange(prevRoomId: string | undefined, currRoomId: string): void {
    if (prevRoomId && !this.isEditing) {
      this.saveRoomContent(prevRoomId, this.getEditorContent());
    }

    if (currRoomId) {
      const savedContent = this.getRoomContent(currRoomId);
      this.setEditorContent(savedContent ?? '');
    }
  }

  private getMentions(): string[] | undefined {
    const nodes = this.editor.$nodes('mention');
    if (!nodes) {
      return undefined;
    }

    const mentions: string[] = [];

    nodes.forEach(node => {
      if (node.attributes['target'] === 'room') {
        mentions.push('room');
      } else {
        mentions.push(node.attributes['id'] as string);
      }
    });

    return mentions.length > 0 ? mentions : undefined;
  }

  private resetEditorState(): void {
    this.pendingAttachments = [];
    this.fileUpload()?.reset();
    this.editor.chain().clearContent().focus().run();
    this.isEditing = false;
    this.originalMessageId = undefined;
  }

  private syncAttachmentsWithEditor(attachments: PendingAttachment[]): PendingAttachment[] {
    const json = this.editor.getJSON();
    const attachmentIdsInEditor = new Set<string>();

    const collect = (node: any): void => {
      if (!node) return;

      const id = node.attrs?.['attachmentId'];
      if (typeof id === 'string') {
        attachmentIdsInEditor.add(id);
      }

      if (Array.isArray(node.content)) {
        node.content.forEach(collect);
      }
    };

    collect(json);

    return attachments.filter(a => attachmentIdsInEditor.has(a.id));
  }
}
